import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { apiCall } from '@/lib/http/client';

import type { CrmObjectTypeSchema } from '../core/SchemaClient';
import type { EntityTypeName } from '../core/types';

/**
 * Canonical React Query key for the org-wide entity-type schema list.
 *
 * Every consumer of `/api/orgs/<orgId>/crm-events/schemas/object-types/` MUST
 * use this helper rather than constructing the key inline. The orgId is
 * normalized to a number so callers passing the route param (string) and
 * callers passing `currentOrg.id` (number) produce the SAME cache key —
 * preventing duplicate fetches caused by structural-equality mismatch in the
 * TanStack Query cache.
 */
export function entityTypesQueryKey(orgId: string | number | undefined | null) {
  // Normalize undefined/null/empty-string to undefined so disabled queries
  // share a single key. Otherwise coerce to Number for cache stability.
  const normalized =
    orgId === undefined || orgId === null || orgId === ''
      ? undefined
      : Number(orgId);
  return ['object-types-all', normalized] as const;
}

/**
 * API helper for fetching entity type schemas.
 */
export async function fetchEntityTypeSchemas(
  orgId: string | number
): Promise<CrmObjectTypeSchema[]> {
  const url = `/api/orgs/${orgId}/crm-events/schemas/object-types/`;

  // Route through apiCall (not raw fetchWithDefaults) so the centralized 403
  // handling — access-paused / org-deactivated / billing-required redirect +
  // stall — still fires. Imperative callers like GenericCrmList's config
  // resolve use this canonical fetcher; without apiCall a paused/billing-gated
  // user would stay on the page with a generic query error instead of being
  // redirected to the recovery page.
  const result = await apiCall<CrmObjectTypeSchema[]>(url);
  // Tolerate a 204 (apiCall returns {}) by normalizing to an empty array —
  // downstream consumers assume an array.
  return Array.isArray(result) ? result : [];
}

/**
 * Hook to fetch and cache entity type schemas for a specific entity type.
 *
 * Uses the shared `entityTypesQueryKey` so that this hook and every other
 * consumer of `/schemas/object-types/` share the same React Query cache —
 * avoiding duplicate fetches.
 *
 * @param orgId - The organization ID
 * @param entityType - The CRM entity type name (e.g., 'ahoy_contact', 'ahoy_deal')
 * @returns Object containing schema, loading state, and error state
 *
 * @example
 * const { schema, loading, error } = useEntityTypeSchema(orgId, 'ahoy_contact');
 * console.log(schema?.label_plural); // 'Contacts'
 */
export function useEntityTypeSchema(
  orgId: string | number | undefined,
  entityType?: EntityTypeName
) {
  const enabled = !!orgId && !!entityType;

  // Use the canonical shared key so every object-types consumer dedupes.
  const {
    data: schemas,
    isLoading,
    error,
  } = useQuery({
    queryKey: entityTypesQueryKey(orgId),
    queryFn: () => fetchEntityTypeSchemas(orgId as string | number),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minute cache
  });

  // Find the specific entity type schema from the shared cache
  const schema = useMemo(() => {
    if (!schemas || !entityType) {
      return null;
    }
    return schemas.find(s => s.entity_type_name === entityType) || null;
  }, [schemas, entityType]);

  return {
    schema,
    loading: isLoading,
    // Guard error when disabled to prevent stale cached errors from leaking
    error: enabled ? (error as Error | null) : null,
  };
}
